// src/componentes/FormularioContenido.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, ListGroup, Badge, Row, Col } from 'react-bootstrap';

const FormularioContenido = ({ onSubmit, onCancel, initialData, mostrarAlerta }) => {
  const [nuevoContenido, setNuevoContenido] = useState({
    curso: '',
    estado: 'activo',
    código: '',
    contenido: [],
    ...initialData
  });

  const [nuevaTarjeta, setNuevaTarjeta] = useState({
    tituloTarjeta: '',
    descripcionTarjeta: '',
    imagen: '',
    enlace: '',
    tipo: 'externo' // Nuevo campo en cada tarjeta
  });

  // Generar prefijo automático desde el nombre del curso
  const generarPrefijo = useCallback((nombreCurso) => {
    return nombreCurso
      .split(' ')
      .map(palabra => palabra[0]?.toUpperCase() || '')
      .join('')
      .substring(0, 3)
      .padEnd(3, 'X');
  }, []);

  // Generar código completo con prefijo + HHmmss
  const generarCodigo = useCallback(() => {
    const ahora = new Date();
    const timestamp = [
      ahora.getHours().toString().padStart(2, "0"),
      ahora.getMinutes().toString().padStart(2, "0"),
      ahora.getSeconds().toString().padStart(2, "0")
    ].join("");
    return `${generarPrefijo(nuevoContenido.curso)}-${timestamp}`;
  }, [generarPrefijo, nuevoContenido.curso]);

  // Actualizar código cuando cambia el nombre del curso
  useEffect(() => {
    if (!initialData?.código) {
      setNuevoContenido(prev => ({
        ...prev,
        código: generarCodigo()
      }));
    }
  }, [nuevoContenido.curso, generarCodigo, initialData?.código]);

  // Manejar cambios en campos principales
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if (name === 'curso' || name === 'tituloTarjeta') {
      processedValue = value.toUpperCase().slice(0, 25);
    }

    setNuevoContenido(prev => ({
      ...prev,
      [name]: processedValue,
      ...(name === 'curso' && { código: generarCodigo() })
    }));
  };

  // Nuevo handler para el tipo de tarjeta
  const handleTipoTarjetaChange = (e) => {
    setNuevaTarjeta(prev => ({
      ...prev,
      tipo: e.target.value
    }));
  };

  // Manejar cambios en campos de tarjetas
  const handleTarjetaChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if (name === 'descripcionTarjeta') {
      processedValue = value.toLowerCase().slice(0, 200);
    } else if (name === 'tituloTarjeta') {
      processedValue = value.toUpperCase().slice(0, 25);
    }

    setNuevaTarjeta(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const agregarTarjeta = () => {
    if (nuevaTarjeta.tituloTarjeta && nuevaTarjeta.descripcionTarjeta) {
      setNuevoContenido(prev => ({
        ...prev,
        contenido: [...prev.contenido, nuevaTarjeta]
      }));
      setNuevaTarjeta({
        tituloTarjeta: '',
        descripcionTarjeta: '',
        imagen: '',
        enlace: '',
        tipo: 'externo'
      });
    }
  };

  const eliminarTarjeta = (index) => {
    setNuevoContenido(prev => ({
      ...prev,
      contenido: prev.contenido.filter((_, i) => i !== index)
    }));
  };

  const validarFormulario = () => {
    return (
      nuevoContenido.curso.trim() &&
      nuevoContenido.contenido.length > 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) {
      mostrarAlerta('error', 'Error', 'Complete los campos obligatorios');
      return;
    }

    try {
      const contenidoFirestore = {
        ...nuevoContenido,
        visible: nuevoContenido.estado === 'activo',
        contenido: nuevoContenido.contenido.map(tarjeta => ({
          ...tarjeta,
          tituloTarjeta: tarjeta.tituloTarjeta.trim(),
          descripcionTarjeta: tarjeta.descripcionTarjeta.trim()
        }))
      };

      await onSubmit(contenidoFirestore);
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="p-3 bg-light rounded-3">
      {/* Sección Principal */}
      <div className="mb-4 border-bottom pb-3">
        <h4 className="mb-3 text-primary">{initialData ? 'Editar' : 'Nuevo'} Contenido</h4>

        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">Nombre del Curso *</Form.Label>
              <Form.Control
                type="text"
                name="curso"
                value={nuevoContenido.curso}
                onChange={handleInputChange}
                placeholder="Ej: DISEÑO DIGITAL"
                className="border-primary"
              />
              <div className="text-end small text-muted mt-1">
                {nuevoContenido.curso.length}/25 caracteres
              </div>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold">Estado</Form.Label>
              <div className="d-flex align-items-center gap-2 bg-white p-2 rounded border">
                <Form.Check
                  type="switch"
                  id="estado-switch"
                  label={nuevoContenido.estado === 'activo' ? 'Activo' : 'Oculto'}
                  checked={nuevoContenido.estado === 'activo'}
                  className="mb-0"
                />
              </div>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label className="fw-bold">Código Generado</Form.Label>
              <Form.Control
                value={nuevoContenido.código}
                readOnly
                className="bg-white border-primary fw-bold"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Sección Tarjetas */}
      <div className="mb-4">
        <h5 className="mb-3 text-primary">Contenido del Curso *</h5>

        <div className="bg-white p-3 rounded border">
          <Row className="g-3">
            <Col md={12} lg={3}>
              <Form.Group>
                <Form.Label>Título *</Form.Label>
                <Form.Control
                  type="text"
                  name="tituloTarjeta"
                  value={nuevaTarjeta.tituloTarjeta}
                  onChange={handleTarjetaChange}
                  placeholder="INTRODUCCIÓN AL CURSO"
                  className="border-primary"
                />
                <div className="small text-muted mt-1">Máx. 25 caracteres</div>
              </Form.Group>
            </Col>

            <Col md={12} lg={5}>
              <Form.Group>
                <Form.Label>Descripción *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcionTarjeta"
                  value={nuevaTarjeta.descripcionTarjeta}
                  onChange={handleTarjetaChange}
                  placeholder="Descripción detallada en minúsculas..."
                  className="border-primary"
                />
                <div className="small text-muted mt-1 text-end">
                  {200 - nuevaTarjeta.descripcionTarjeta.length} caracteres restantes
                </div>
              </Form.Group>
            </Col>

            <Col md={12} lg={2}>
              <Form.Group>
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  value={nuevaTarjeta.tipo}
                  onChange={handleTipoTarjetaChange}
                  className="border-primary"
                >
                  <option value="externo">Externo</option>
                  <option value="interno">Interno</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={12} lg={2}>
              <Form.Group>
                <Form.Label>Imagen</Form.Label>
                <Form.Control
                  type="url"
                  name="imagen"
                  value={nuevaTarjeta.imagen}
                  onChange={handleTarjetaChange}
                  placeholder="https://..."
                  className="border-primary"
                />
              </Form.Group>
            </Col>

            <Col md={12} lg={2}>
              <Form.Group>
                <Form.Label>Enlace</Form.Label>
                <Form.Control
                  type="url"
                  name="enlace"
                  value={nuevaTarjeta.enlace}
                  onChange={handleTarjetaChange}
                  placeholder="https://..."
                  className="border-primary"
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Button
                variant="outline-primary"
                onClick={agregarTarjeta}
                className="w-100 mt-2"
                disabled={!nuevaTarjeta.tituloTarjeta || !nuevaTarjeta.descripcionTarjeta}
              >
                <i className="bi bi-plus-circle me-2"></i>Agregar Tarjeta
              </Button>
            </Col>
          </Row>
        </div>

        {/* Lista de Tarjetas */}
        <div className="mt-3">
          <ListGroup>
            {nuevoContenido.contenido.map((tarjeta, index) => (
              <ListGroup.Item key={index} className="mb-2 border rounded-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="w-75">
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="primary">Título</Badge>
                        <span className="fw-bold">{tarjeta.tituloTarjeta}</span>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="secondary">Descripción</Badge>
                        <span className="text-muted">{tarjeta.descripcionTarjeta}</span>
                      </div>

                      {tarjeta.tipo && (
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={tarjeta.tipo === 'interno' ? 'warning' : 'secondary'}>
                            {tarjeta.tipo.toUpperCase()}
                          </Badge>
                        </div>
                      )}

                      {tarjeta.imagen && (
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="info">Imagen</Badge>
                          <small className="text-break">{tarjeta.imagen}</small>
                        </div>
                      )}

                      {tarjeta.enlace && (
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="success">Enlace</Badge>
                          <small className="text-break">{tarjeta.enlace}</small>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => eliminarTarjeta(index)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg"
                         width="20"
                         height="20"
                         fill="currentColor"
                         className="bi bi-trash3-fill"
                         viewBox="0 0 16 16">
                      <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
                    </svg>
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      </div>

      {/* Botones Finales */}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          variant="outline-secondary"
          onClick={onCancel}
          className="px-4"
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={!validarFormulario()}
          className="px-4"
        >
          {initialData ? 'Actualizar' : 'Crear Contenido'}
        </Button>
      </div>
    </Form>
  );
};

export default FormularioContenido;
